// LoginPage — email+password fallback for environments where the OIDC
// PKCE flow can't run (e.g. browsers with SES/lockdown extensions that
// strip crypto.subtle, locked-down CSP that blocks the redirect chain,
// or kiosk profiles without WebCrypto). POSTs to Nova OS's
// /api/auth/login endpoint and stores the access_token in sessionStorage
// so the rest of the app picks up the authenticated state on the next
// render via useAuth().refresh().
//
// Mounted OUTSIDE <ProtectedRoute> in routes.tsx so the form actually
// renders for unauthenticated users (the ProtectedRoute wrapper would
// otherwise kick off the broken OIDC redirect before this page rendered).

import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTenant } from "@/tenants";
import { useAuth } from "../../auth/OidcProvider";
import { storeToken } from "../../auth/token";

interface LocationState {
  from?: string;
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const tenant = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefer ?from= query (used by the SES-fallback redirect from
  // oauthClient.beginLogin) over location.state (set when navigated via
  // React Router with state). Default to / when neither is present.
  const queryFrom = new URLSearchParams(location.search).get("from");
  const stateFrom = (location.state as LocationState | null)?.from;
  const from = queryFrom ?? stateFrom ?? "/";

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as {
          error?: string | { message?: string };
        };
        const msg =
          typeof body.error === "string"
            ? body.error
            : body.error?.message ?? `login failed (${r.status})`;
        throw new Error(msg);
      }
      const body = (await r.json()) as {
        access_token?: string;
        id_token?: string;
      };
      const token = body.access_token ?? body.id_token;
      if (!token) throw new Error("login response missing access_token");
      storeToken(token);
      refresh();
      navigate(from, { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm rounded border border-slate-200 bg-white p-6">
        <h1 className="mb-1 text-lg font-semibold">{t("login.title")}</h1>
        <p className="mb-4 text-xs text-slate-500">{t("login.subtitle")}</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block">
            <span className="text-xs text-slate-500">{t("login.email")}</span>
            <input
              type="email"
              autoFocus
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded border border-slate-200 bg-slate-100 px-2 py-1 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">{t("login.password")}</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded border border-slate-200 bg-slate-100 px-2 py-1 text-sm"
            />
          </label>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded bg-blue-100 px-3 py-2 text-sm text-blue-600 hover:bg-blue-200 disabled:opacity-50"
          >
            {submitting ? t("login.submitting") : t("login.submit")}
          </button>
        </form>
        {tenant.brand.poweredBy && (
          <p className="mt-5 text-center text-[11px] tracking-wide text-slate-400">
            <span className="inline-flex items-center gap-1">Powered by <img src="/libraos-mark.svg" alt="" className="h-3.5 w-3.5" /><span className="font-semibold">{tenant.brand.poweredBy}</span></span>
          </p>
        )}
      </div>
    </div>
  );
}
